//src/pages/Usuarios.js
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "../axiosConfig";
import {
  Edit3,
  KeyRound,
  Mail,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
  UserRoundCog,
  Users,
} from "lucide-react";

import ModalSemPermissao from "../components/ModalSemPermissao";
import { toast } from "react-toastify";
import "./Usuarios.css";

function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [loading, setLoading] = useState(false);

  const itensPorPagina = 50;

  const usuarioLogado = JSON.parse(localStorage.getItem("usuario"));

  const podeVerUsuarios =
    usuarioLogado?.tipo === "admin" ||
    usuarioLogado?.permissoes?.some(
      (p) => p.rota === "usuarios" && p.pode_ler
    );

  const podeEditarUsuarios =
    usuarioLogado?.tipo === "admin" ||
    usuarioLogado?.permissoes?.some(
      (p) => p.rota === "usuarios" && p.pode_editar
    );

  const podeExcluirUsuarios =
    usuarioLogado?.tipo === "admin" ||
    usuarioLogado?.permissoes?.some(
      (p) => p.rota === "usuarios" && p.pode_excluir
    );

  const podeInserirUsuarios =
    usuarioLogado?.tipo === "admin" ||
    usuarioLogado?.permissoes?.some(
      (p) => p.rota === "usuarios" && p.pode_inserir
    );

  const fetchUsuarios = async () => {
    setLoading(true);

    try {
      const response = await axios.get("/usuarios-adm");

      if (Array.isArray(response.data)) {
        setUsuarios(response.data);
      } else {
        console.error("Resposta inesperada:", response.data);
        setUsuarios([]);
      }
    } catch (error) {
      console.error("Erro ao buscar usuários:", error);
      toast.error("Erro ao buscar usuários.");
      setUsuarios([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsuarios();
  }, []);

  const excluirUsuario = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este usuário?")) return;

    setLoading(true);

    try {
      const res = await axios.delete(`/usuarios/${id}`);
      toast.success(res.data.message || "Usuário excluído com sucesso!");
      await fetchUsuarios();
    } catch (err) {
      console.error("Erro ao excluir:", err);
      toast.error(err.response?.data?.error || "Erro ao excluir usuário.");
    } finally {
      setLoading(false);
    }
  };

  const normalizar = (valor) =>
    String(valor || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  const usuariosFiltrados = useMemo(() => {
    const termo = normalizar(filtro);

    return usuarios.filter((usuario) => {
      const texto = normalizar(
        [
          usuario.id,
          usuario.nome,
          usuario.email,
          usuario.codigo,
          usuario.tipo,
          usuario.status,
        ].join(" ")
      );

      return !termo || texto.includes(termo);
    });
  }, [usuarios, filtro]);

  const totalPaginas = Math.max(
    1,
    Math.ceil(usuariosFiltrados.length / itensPorPagina)
  );

  const usuariosPaginados = usuariosFiltrados.slice(
    (paginaAtual - 1) * itensPorPagina,
    paginaAtual * itensPorPagina
  );

  const mudarPagina = (novaPagina) => {
    if (novaPagina >= 1 && novaPagina <= totalPaginas) {
      setPaginaAtual(novaPagina);
    }
  };

  const resumo = useMemo(() => {
    const total = usuarios.length;
    const admins = usuarios.filter((u) => u.tipo === "admin").length;
    const ativos = usuarios.filter((u) => u.status === "ativo").length;
    const inativos = usuarios.filter((u) => u.status === "inativo").length;

    return { total, admins, ativos, inativos };
  }, [usuarios]);

  if (!podeVerUsuarios) {
    return <ModalSemPermissao />;
  }

  return (
    <div className="usuariosAdmin-page">
      <div className="usuariosAdmin-topbar">
        <div>
          <span className="usuariosAdmin-kicker">Área administrativa</span>
          <h2>Usuários do Sistema</h2>
          <p>
            Gerencie operadores, administradores, permissões de acesso e status
            dos usuários internos.
          </p>
        </div>

        {podeInserirUsuarios && (
          <Link to="/usuarios/novo" className="usuariosAdmin-btn primary">
            <Plus size={16} />
            Cadastrar usuário
          </Link>
        )}
      </div>

      <div className="usuariosAdmin-cards">
        <div className="usuariosAdmin-card">
          <span>Total de usuários</span>
          <strong>{resumo.total.toLocaleString("pt-BR")}</strong>
        </div>

        <div className="usuariosAdmin-card admin">
          <span>Administradores</span>
          <strong>{resumo.admins.toLocaleString("pt-BR")}</strong>
        </div>

        <div className="usuariosAdmin-card ativo">
          <span>Ativos</span>
          <strong>{resumo.ativos.toLocaleString("pt-BR")}</strong>
        </div>

        <div className="usuariosAdmin-card inativo">
          <span>Inativos</span>
          <strong>{resumo.inativos.toLocaleString("pt-BR")}</strong>
        </div>
      </div>

      <div className="usuariosAdmin-filtros">
        <div className="usuariosAdmin-search">
          <Search size={15} />
          <input
            type="text"
            placeholder="Filtrar por nome, e-mail, código, tipo ou status..."
            value={filtro}
            onChange={(e) => {
              setFiltro(e.target.value);
              setPaginaAtual(1);
            }}
          />
        </div>
      </div>

      <div className="usuariosAdmin-tableCard">
        <div className="usuariosAdmin-tableHeader">
          <div>
            <h3>Lista de usuários</h3>
            <span>
              {usuariosFiltrados.length.toLocaleString("pt-BR")} resultado(s)
            </span>
          </div>

          <Users size={18} />
        </div>

        <div className="usuariosAdmin-tableWrapper">
          <table className="usuariosAdmin-table">
            <thead>
              <tr>
                <th>Usuário</th>
                <th>E-mail</th>
                <th>Código</th>
                <th>Tipo</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="usuariosAdmin-empty">
                    Carregando usuários...
                  </td>
                </tr>
              ) : usuariosPaginados.length === 0 ? (
                <tr>
                  <td colSpan="6" className="usuariosAdmin-empty">
                    Nenhum usuário encontrado.
                  </td>
                </tr>
              ) : (
                usuariosPaginados.map((usuario) => (
                  <tr key={usuario.id}>
                    <td>
                      <div className="usuariosAdmin-userCell">
                        <div className="usuariosAdmin-avatar">
                          {usuario.nome?.charAt(0)?.toUpperCase() || "U"}
                        </div>

                        <div>
                          <strong>{usuario.nome || "—"}</strong>
                          <span>ID #{usuario.id}</span>
                        </div>
                      </div>
                    </td>

                    <td>
                      <div className="usuariosAdmin-email">
                        <Mail size={14} />
                        <span>{usuario.email || "—"}</span>
                      </div>
                    </td>

                    <td>{usuario.codigo || "—"}</td>

                    <td>
                      <span
                        className={`usuariosAdmin-badge tipo ${
                          usuario.tipo === "admin" ? "admin" : "padrao"
                        }`}
                      >
                        <ShieldCheck size={12} />
                        {usuario.tipo || "—"}
                      </span>
                    </td>

                    <td>
                      <span
                        className={`usuariosAdmin-badge status ${
                          usuario.status === "ativo" ? "ativo" : "inativo"
                        }`}
                      >
                        {usuario.status || "—"}
                      </span>
                    </td>

                    <td>
                      <div className="usuariosAdmin-actions">
                        {podeEditarUsuarios && (
                          <Link
                            to={`/usuarios/editar/${usuario.id}`}
                            className="usuariosAdmin-actionBtn edit"
                          >
                            <Edit3 size={14} />
                            Editar
                          </Link>
                        )}

                        {podeEditarUsuarios && (
                          <Link
                            to={`/usuarios/${usuario.id}/permissoes`}
                            className="usuariosAdmin-actionBtn permission"
                          >
                            <KeyRound size={14} />
                            Permissões
                          </Link>
                        )}

                        {podeExcluirUsuarios && (
                          <button
                            type="button"
                            className="usuariosAdmin-actionBtn delete"
                            onClick={() => excluirUsuario(usuario.id)}
                          >
                            <Trash2 size={14} />
                            Excluir
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPaginas > 1 && (
        <div className="usuariosAdmin-paginacao">
          <button
            onClick={() => mudarPagina(paginaAtual - 1)}
            disabled={paginaAtual === 1}
          >
            Anterior
          </button>

          <span>
            Página {paginaAtual} de {totalPaginas}
          </span>

          <button
            onClick={() => mudarPagina(paginaAtual + 1)}
            disabled={paginaAtual === totalPaginas}
          >
            Próxima
          </button>
        </div>
      )}
    </div>
  );
}

export default Usuarios;